import { useClientPortalAPI } from "@certn/api";
import { components } from "@certn/api/generated-schemas/client-portal";
import { Modal, showToast, Text } from "@certn/cascadia-react";
import { EVENT_NAMES, slugify, useEventTracking } from "@certn/utils";
import { css } from "@emotion/css";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import { BundleConflictModal } from "./BundleConflictModal";
import { BundleDetails } from "./BundleDetails";
import { CheckConflictModal } from "./CheckConflictModal";
import { Bundle } from "../../../../components/bundle/Bundle";
import {
  OrderContext,
  SelectedBundlesType,
  SelectedChecksType,
} from "../../OrderContext";

interface Props {
  group: string | null;
  quickscreenOnly?: boolean;
}

export function BundleSection({ group, quickscreenOnly }: Props) {
  const { t } = useTranslation();
  const { trackEvent } = useEventTracking();
  const { order, setOrder } = useContext(OrderContext);
  const [selectedBundle, setSelectedBundle] = useState<SelectedBundlesType>();
  const [isCheckConflictModalOpen, setIsCheckConflictModalOpen] =
    useState(false);
  const [isBundleConflictModalOpen, setIsBundleConflictModalOpen] =
    useState(false);
  const [checkArgs, setCheckArgs] = useState<
    Record<string, Record<string, unknown>>
  >({});
  const { data: bundles } = useClientPortalAPI(
    group
      ? `/api/internal/client-portal/customer-groups/${group}/available-bundles/`
      : null,
    { is_quickscreen: quickscreenOnly ?? false }
  );
  const handleUpdateArguments = (
    check_arguments: Record<string, unknown>,
    check: components["schemas"]["BundledDealCheckListChild"]
  ) => {
    setCheckArgs((prev) => ({ ...prev, [check.type]: check_arguments }));
  };
  const hasConflictingChecks = (bundle: SelectedBundlesType) => {
    if (!order.checks.length) {
      return false;
    }

    const orderChecks = order.checks.map((check) => check.key);
    const bundleChecks = bundle.checks?.map((check) => check.type) || [];
    const combinedChecks = [...orderChecks, ...bundleChecks];

    return new Set(combinedChecks).size !== combinedChecks.length;
  };

  const hasConflictingBundles = (bundle: SelectedBundlesType) => {
    if (!order.bundles.length) {
      return false;
    }

    const orderBundleChecks = order.bundles.flatMap(
      (orderBundle) => orderBundle.checks?.map(({ type }) => type) || []
    );
    const bundleChecks = bundle.checks?.map((check) => check.type) || [];
    const combinedChecks = [...orderBundleChecks, ...bundleChecks];

    return new Set(combinedChecks).size !== combinedChecks.length;
  };

  const addBundle = (
    bundle: SelectedBundlesType,
    checksToUpdate?: SelectedChecksType[],
    bundlesToUpdate?: SelectedBundlesType[]
  ) => {
    // Get check_types_with_arguments from bundle (comes from API)
    const bundleCheckArgs = (bundle as any).check_types_with_arguments || {};

    const updatedBundle = {
      ...bundle,
      checks: bundle?.checks?.map((check) => {
        // Merge: bundle's args as base, user modifications on top
        const argsFromBundle = bundleCheckArgs[check.type] || {};
        const userArgs = checkArgs[check.type] || {};
        // Bundle args (like mode: "country") + user args merged on top
        const finalArgs = { ...argsFromBundle, ...userArgs };
        return {
          ...check,
          check_arguments: finalArgs,
        };
      }),
    };

    const newOrder = {
      ...order,
      bundles: [...order.bundles, updatedBundle],
    };

    if (checksToUpdate) {
      newOrder.checks = checksToUpdate;
    }

    if (bundlesToUpdate) {
      newOrder.bundles = [...bundlesToUpdate, updatedBundle];
    }

    setOrder(newOrder);
    trackEvent(EVENT_NAMES.CP_BUNDLED_DEAL, {
      bundledDeal: bundle.title,
    });
    showToast(t("{{bundleName}} added", { bundleName: bundle.title }), {
      type: "success",
    });
    setSelectedBundle(undefined);
  };

  const onAdd = (bundle?: SelectedBundlesType) => {
    if (!bundle) {
      showToast(t("Something went wrong, try again"), {
        type: "error",
      });
      return;
    }

    if (hasConflictingChecks(bundle)) {
      setSelectedBundle(bundle);
      setIsCheckConflictModalOpen(true);
      return;
    }

    if (hasConflictingBundles(bundle)) {
      setSelectedBundle(bundle);
      setIsBundleConflictModalOpen(true);
      return;
    }

    addBundle(bundle);
  };

  const onRemove = (bundle?: SelectedBundlesType) => {
    if (!bundle) {
      showToast(t("Something went wrong, try again"), {
        type: "error",
      });
      return;
    }

    const newBundles = [...order.bundles.filter(({ id }) => id !== bundle.id)];
    setOrder({
      ...order,
      bundles: [...newBundles],
    });
    showToast(t("{{bundleName}} removed", { bundleName: bundle.title }), {
      type: "success",
    });
    setSelectedBundle(undefined);
  };

  if (!bundles?.length) {
    return null;
  }

  const onConfirmCheckConflictResolution = () => {
    if (!selectedBundle) {
      showToast(t("Something went wrong, try again"), {
        type: "error",
      });
      return;
    }

    const bundleChecks =
      selectedBundle?.checks?.map((check) => check.type) || [];
    const newOrderChecks: SelectedChecksType[] = [];

    order.checks.forEach((check) => {
      if (bundleChecks.includes(check.key)) {
        return;
      }

      newOrderChecks.push(check);
    });

    if (hasConflictingBundles(selectedBundle)) {
      onConfirmBundleConflictResolution(newOrderChecks);
      return;
    }

    addBundle(selectedBundle, newOrderChecks);

    setIsCheckConflictModalOpen(false);
    setSelectedBundle(undefined);
  };

  const onConfirmBundleConflictResolution = (
    newOrderChecks?: SelectedChecksType[]
  ) => {
    if (!selectedBundle) {
      showToast(t("Something went wrong, try again"), {
        type: "error",
      });
      return;
    }

    const selectedBundleChecks =
      selectedBundle?.checks?.map((check) => check.type) || [];
    const newOrderBundles: SelectedBundlesType[] = [];

    order.bundles.forEach((bundle) => {
      const bundleCheckIds = bundle.checks?.map((check) => check.type) || [];
      const combinedChecks = [...bundleCheckIds, ...selectedBundleChecks];

      if (new Set(combinedChecks).size !== combinedChecks.length) {
        return;
      }

      newOrderBundles.push(bundle);
    });

    addBundle(selectedBundle, newOrderChecks, newOrderBundles);

    setIsCheckConflictModalOpen(false);
    setIsBundleConflictModalOpen(false);
    setSelectedBundle(undefined);
  };

  return (
    <>
      <div className={styles.container}>
        <Text style="global/heading-large">{t("Bundled deals")}</Text>
        <div className={styles.bundles}>
          {bundles.map((bundle) => (
            <Bundle
              key={bundle.id}
              bundleName={bundle.title}
              checks={bundle.products.map((product) => product.name)}
              price={bundle.total ? bundle.total.toString() : ""}
              onPress={() => setSelectedBundle(bundle)}
              isSelected={Boolean(
                order.bundles?.find(({ id }) => id === bundle?.id)
              )}
              testID={slugify(bundle.title)}
              currency={bundle.currency}
            />
          ))}
        </div>
      </div>
      <Modal
        isOpen={Boolean(selectedBundle)}
        className={styles.modalContainer}
        testID="bundle-details-modal"
      >
        <BundleDetails
          bundle={selectedBundle}
          isBundleApplied={
            !!order.bundles?.find(({ id }) => id === selectedBundle?.id)
          }
          onClose={() => setSelectedBundle(undefined)}
          onAdd={onAdd}
          onRemove={onRemove}
          handleUpdateArguments={handleUpdateArguments}
        />
        <CheckConflictModal
          isOpen={isCheckConflictModalOpen}
          isLoading={false}
          onCancel={() => setIsCheckConflictModalOpen(false)}
          onConfirm={onConfirmCheckConflictResolution}
        />
        <BundleConflictModal
          isOpen={isBundleConflictModalOpen}
          isLoading={false}
          onCancel={() => setIsBundleConflictModalOpen(false)}
          onConfirm={onConfirmBundleConflictResolution}
        />
      </Modal>
    </>
  );
}

const styles = {
  container: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-3)",
  }),
  bundles: css({
    display: "flex",
    gap: "var(--space-4)",
    flexWrap: "wrap",
  }),
  modalContainer: css({
    width: "100%",
  }),
};
